import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { PermissionsConfig } from '../../../hooks/usePermissionsConfig';
import { SOVEREIGN } from '../../epc/utils/epcProjectVisual';

export interface PermissionMatrixProps {
  value: Record<string, string[]>;
  onChange: (next: Record<string, string[]>) => void;
  config: PermissionsConfig;
  disabled?: boolean;
  search?: string;
}

interface CellState {
  checked: boolean;
  supported: boolean;
}

interface ChevronState {
  checked: boolean;
  indeterminate: boolean;
}

function getModuleActions(value: Record<string, string[]>, moduleKey: string): string[] {
  return Array.isArray(value[moduleKey]) ? value[moduleKey] : [];
}

function rowChevron(
  value: Record<string, string[]>,
  moduleKey: string,
  supportedActions: string[],
): ChevronState {
  if (supportedActions.length === 0) return { checked: false, indeterminate: false };
  const current = new Set(getModuleActions(value, moduleKey));
  const selected = supportedActions.filter((a) => current.has(a)).length;
  return {
    checked: selected === supportedActions.length,
    indeterminate: selected > 0 && selected < supportedActions.length,
  };
}

function columnChevron(
  value: Record<string, string[]>,
  actionKey: string,
  modules: PermissionsConfig['modules'],
): ChevronState {
  const supporting = modules.filter((m) => m.actions.includes(actionKey));
  if (supporting.length === 0) return { checked: false, indeterminate: false };
  const selected = supporting.filter((m) => getModuleActions(value, m.key).includes(actionKey)).length;
  return {
    checked: selected === supporting.length,
    indeterminate: selected > 0 && selected < supporting.length,
  };
}

function grantAllChevron(
  value: Record<string, string[]>,
  modules: PermissionsConfig['modules'],
): ChevronState {
  let total = 0;
  let selected = 0;
  for (const m of modules) {
    total += m.actions.length;
    const cur = new Set(getModuleActions(value, m.key));
    for (const a of m.actions) if (cur.has(a)) selected++;
  }
  if (total === 0) return { checked: false, indeterminate: false };
  return { checked: selected === total, indeterminate: selected > 0 && selected < total };
}

/**
 * Tri-state checkbox matching the existing app style (green #334E41). Renders a real
 * native input under the hood so the indeterminate property is settable via ref.
 */
function TriCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
  ariaLabel,
  title,
  tabIndex,
  onKeyDown,
  inputRef,
  dataTestId,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
  title?: string;
  tabIndex?: number;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  dataTestId?: string;
}) {
  const localRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (localRef.current) {
      localRef.current.indeterminate = !!indeterminate && !checked;
    }
  }, [indeterminate, checked]);

  return (
    <input
      ref={(el) => {
        localRef.current = el;
        inputRef?.(el);
      }}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      aria-checked={indeterminate && !checked ? 'mixed' : checked}
      title={title}
      data-testid={dataTestId}
      className="w-4 h-4 cursor-pointer accent-[#1F5A47] disabled:cursor-not-allowed disabled:opacity-40"
    />
  );
}

/**
 * <PermissionMatrix> — rows=modules, columns=actions.
 *
 * Layout notes:
 * - Top-left corner cell holds the "Grant everything" tri-state checkbox.
 * - Header row: one cell per action in `config.actionCatalog`, each with a column select-all checkbox.
 * - Each module row: first cell shows the module name (with description tooltip via title=) AND
 *   a small row select-all checkbox; subsequent cells are per-action checkboxes. Cells for
 *   actions the module doesn't support are rendered greyed out and non-interactive.
 *
 * Keyboard: arrow keys move focus among interactive checkboxes in the grid. Tab leaves the grid.
 */
export default function PermissionMatrix({
  value,
  onChange,
  config,
  disabled = false,
  search = '',
}: PermissionMatrixProps) {
  const modules = config.modules ?? [];
  const actionCatalog = config.actionCatalog ?? [];

  const filteredModules = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return modules;
    return modules.filter((m) => (m.name || m.key).toLowerCase().includes(q));
  }, [modules, search]);

  // ref grid for keyboard nav. Rows: 0 = header, 1..n = data rows (+1 grant-all row not in grid here).
  // Cols: 0 = row-select-all column; 1..k = action columns.
  const cellRefs = useRef<Array<Array<HTMLInputElement | null>>>([]);
  const [focus, setFocus] = useState<{ row: number; col: number }>({ row: 0, col: 0 });

  const totalCols = actionCatalog.length + 1;
  const totalRows = filteredModules.length + 1;

  const ensureRow = (rowIdx: number) => {
    if (!cellRefs.current[rowIdx]) cellRefs.current[rowIdx] = [];
  };

  const setCellRef = (rowIdx: number, colIdx: number) => (el: HTMLInputElement | null) => {
    ensureRow(rowIdx);
    cellRefs.current[rowIdx][colIdx] = el;
  };

  const moveFocus = useCallback(
    (nextRow: number, nextCol: number) => {
      const r = Math.max(0, Math.min(totalRows - 1, nextRow));
      const c = Math.max(0, Math.min(totalCols - 1, nextCol));
      setFocus({ row: r, col: c });
      const el = cellRefs.current[r]?.[c];
      if (el) el.focus();
    },
    [totalRows, totalCols],
  );

  const handleKeyDown = (row: number, col: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        moveFocus(row, col + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        moveFocus(row, col - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        moveFocus(row + 1, col);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveFocus(row - 1, col);
        break;
      default:
        break;
    }
  };

  const handleCellToggle = (moduleKey: string, actionKey: string, next: boolean) => {
    const current = getModuleActions(value, moduleKey);
    const set = new Set(current);
    if (next) set.add(actionKey);
    else set.delete(actionKey);
    onChange({ ...value, [moduleKey]: Array.from(set) });
  };

  const handleRowToggle = (mod: PermissionsConfig['modules'][number], next: boolean) => {
    const supported = mod.actions;
    const current = getModuleActions(value, mod.key);
    if (next) {
      const merged = Array.from(new Set([...current, ...supported]));
      onChange({ ...value, [mod.key]: merged });
    } else {
      const kept = current.filter((a) => !supported.includes(a));
      onChange({ ...value, [mod.key]: kept });
    }
  };

  const handleColumnToggle = (actionKey: string, next: boolean) => {
    const nextValue: Record<string, string[]> = { ...value };
    for (const m of modules) {
      if (!m.actions.includes(actionKey)) continue;
      const current = new Set(getModuleActions(nextValue, m.key));
      if (next) current.add(actionKey);
      else current.delete(actionKey);
      nextValue[m.key] = Array.from(current);
    }
    onChange(nextValue);
  };

  const handleGrantAllToggle = (next: boolean) => {
    const nextValue: Record<string, string[]> = { ...value };
    for (const m of modules) {
      nextValue[m.key] = next ? [...m.actions] : [];
    }
    onChange(nextValue);
  };

  const grantAll = grantAllChevron(value, modules);

  return (
    <div
      className="overflow-auto rounded-md"
      style={{ border: `1px solid ${SOVEREIGN.cardBorder}` }}
    >
      <table
        role="grid"
        aria-colcount={totalCols + 1}
        aria-rowcount={totalRows + 1}
        className="min-w-full text-sm border-collapse"
        data-testid="permission-matrix"
      >
        <thead className="sticky top-0 z-10" style={{ background: SOVEREIGN.paper }}>
          <tr role="row">
            <th
              role="columnheader"
              className="sticky left-0 z-20 px-3 py-2 text-left"
              style={{
                background: SOVEREIGN.paper,
                borderBottom: `1px solid ${SOVEREIGN.innerRule}`,
                borderRight: `1px solid ${SOVEREIGN.innerRule}`,
              }}
            >
              <div className="flex items-center gap-2">
                <TriCheckbox
                  checked={grantAll.checked}
                  indeterminate={grantAll.indeterminate}
                  disabled={disabled}
                  onChange={handleGrantAllToggle}
                  ariaLabel="Grant everything"
                  title="Grant everything"
                  tabIndex={0}
                  dataTestId="permission-matrix-grant-all"
                />
                <span
                  className="text-xs font-semibold uppercase tracking-wide"
                  style={{ color: SOVEREIGN.title }}
                >
                  Module
                </span>
              </div>
            </th>
            <th
              role="columnheader"
              className="px-3 py-2 text-xs font-semibold uppercase tracking-wide"
              style={{
                borderBottom: `1px solid ${SOVEREIGN.innerRule}`,
                color: SOVEREIGN.title,
              }}
            >
              All
            </th>
            {actionCatalog.map((a) => {
              const col = columnChevron(value, a.key, modules);
              return (
                <th
                  key={a.key}
                  role="columnheader"
                  className="px-3 py-2 text-left"
                  style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
                  title={a.description}
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <TriCheckbox
                        checked={col.checked}
                        indeterminate={col.indeterminate}
                        disabled={disabled}
                        onChange={(next) => handleColumnToggle(a.key, next)}
                        ariaLabel={`Select all in column ${a.name}`}
                        title="Select all in column"
                        dataTestId={`permission-matrix-col-${a.key}`}
                      />
                      <span
                        className="text-xs font-semibold"
                        style={{ color: SOVEREIGN.title }}
                      >
                        {a.name}
                      </span>
                    </div>
                    {a.description ? (
                      <span
                        className="text-[10px] font-normal normal-case"
                        style={{ color: SOVEREIGN.kpiCaption }}
                      >
                        {a.description}
                      </span>
                    ) : null}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredModules.length === 0 ? (
            <tr role="row">
              <td
                role="gridcell"
                colSpan={totalCols + 1}
                className="px-3 py-4 text-center text-sm"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                No modules match the search.
              </td>
            </tr>
          ) : (
            filteredModules.map((mod, idx) => {
              const rowIdx = idx + 1;
              const rowState = rowChevron(value, mod.key, mod.actions);
              return (
                <tr key={mod.key} role="row" data-testid={`permission-matrix-row-${mod.key}`}>
                  <td
                    role="rowheader"
                    className="sticky left-0 z-10 px-3 py-2"
                    style={{
                      background: SOVEREIGN.cardBg,
                      borderBottom: `1px solid ${SOVEREIGN.innerRule}`,
                      borderRight: `1px solid ${SOVEREIGN.innerRule}`,
                    }}
                    title={mod.description}
                  >
                    <span
                      className="text-sm font-medium"
                      style={{ color: SOVEREIGN.ink }}
                    >
                      {mod.name || mod.key}
                    </span>
                  </td>
                  <td
                    role="gridcell"
                    className="px-3 py-2"
                    style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
                  >
                    <TriCheckbox
                      checked={rowState.checked}
                      indeterminate={rowState.indeterminate}
                      disabled={disabled || mod.actions.length === 0}
                      onChange={(next) => handleRowToggle(mod, next)}
                      ariaLabel={`Select all in row ${mod.name || mod.key}`}
                      title="Select all in row"
                      tabIndex={focus.row === rowIdx && focus.col === 0 ? 0 : -1}
                      onKeyDown={handleKeyDown(rowIdx, 0)}
                      inputRef={setCellRef(rowIdx, 0)}
                      dataTestId={`permission-matrix-row-toggle-${mod.key}`}
                    />
                  </td>
                  {actionCatalog.map((a, aIdx) => {
                    const colIdx = aIdx + 1;
                    const supported = mod.actions.includes(a.key);
                    const granted = getModuleActions(value, mod.key).includes(a.key);
                    // Stale = the role already has this permission but the module no
                    // longer (or never) lists it as a supported action. Show it as a
                    // checked, warning-styled cell so admins can see and unchecking
                    // cleans it up. New grants outside mod.actions are still blocked
                    // upstream via handleRowToggle / handleColumnToggle.
                    const stale = !supported && granted;
                    if (!supported && !stale) {
                      return (
                        <td
                          key={a.key}
                          role="gridcell"
                          aria-disabled="true"
                          data-testid={`permission-matrix-cell-${mod.key}-${a.key}-disabled`}
                          className="px-3 py-2 text-center select-none"
                          style={{
                            borderBottom: `1px solid ${SOVEREIGN.innerRule}`,
                            background: SOVEREIGN.paper,
                            color: SOVEREIGN.barTrack,
                          }}
                          title={`${a.name} is not supported by ${mod.name || mod.key}`}
                        >
                          —
                        </td>
                      );
                    }
                    const checked = granted;
                    return (
                      <td
                        key={a.key}
                        role="gridcell"
                        className="px-3 py-2 text-center"
                        style={{
                          borderBottom: `1px solid ${SOVEREIGN.innerRule}`,
                          background: stale
                            ? 'rgba(201,169,97,0.10)'
                            : undefined,
                        }}
                        title={stale
                          ? `Stale: ${a.name} is no longer declared by ${mod.name || mod.key}. Uncheck to clean up.`
                          : undefined}
                      >
                        <TriCheckbox
                          checked={checked}
                          disabled={disabled}
                          onChange={(next) => handleCellToggle(mod.key, a.key, next)}
                          ariaLabel={`${mod.name || mod.key} ${a.name}${stale ? ' (stale)' : ''}`}
                          title={stale
                            ? `Stale permission — uncheck to remove`
                            : `${mod.name || mod.key}: ${a.name}`}
                          tabIndex={focus.row === rowIdx && focus.col === colIdx ? 0 : -1}
                          onKeyDown={handleKeyDown(rowIdx, colIdx)}
                          inputRef={setCellRef(rowIdx, colIdx)}
                          dataTestId={`permission-matrix-cell-${mod.key}-${a.key}${stale ? '-stale' : ''}`}
                        />
                        {stale && (
                          <span
                            aria-hidden="true"
                            className="ml-1 text-[10px] font-semibold"
                            style={{ color: SOVEREIGN.champagne }}
                          >
                            ⚠
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
